import 'moment/locale/cs';
import 'moment/locale/en-gb';
import 'moment/locale/sk';
import 'moment/locale/hu';
import 'moment/locale/pl';
import 'moment/locale/es';
import 'moment/locale/de';
import 'moment/locale/it';
import 'moment/locale/fr';
import 'moment/locale/pt-br';

import React, { useEffect, useState } from 'react';
import { Redirect, Switch, useParams, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { ContentLoading } from '../components/ui/ContentLoading';
import { CALENDAR_VIEW_LIST } from '../constants';
import { useOfficeByDomain } from '../hooks/apicalls/public/useOfficeByDomain';
import { useViewport } from '../hooks/useViewport';
import { currentPublicViewStudioVar } from '../lib/apollo/cache';
import { ErrorNotFoundStudio } from '../pages/errors';
import { lazyRetry } from '../util/codeSplitting';
import { studioRoutes } from './constants';
import { PublicOfficeRoute } from './routes/PublicOfficeRoute';

const OfficeCoursePublicDetail = React.lazy(() =>
    lazyRetry(
        () => import('../components/calendar-public/course/OfficeCoursePublicDetail'),
        'officeCoursePublicDetail',
    ),
);

const IdentityHeader = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/IdentityHeader/IdentityHeader'), 'identityHeader'),
);

const PassSection = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/PricesAndPayments/PassSection'), 'passSection'),
);

const VouchersSection = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/vouchers/VouchersSection'), 'voucherSection'),
);

const ClaimVoucherSection = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/vouchers/ClaimVouchersSection'), 'claimVoucherSection'),
);

const TeacherProfile = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/TeacherProfile'), 'teacherProfile'),
);

const TermsAndConditionsPublic = React.lazy(() =>
    lazyRetry(
        () => import('../components/calendar-public/TermsAndConditions/TermsAndConditionsPublic'),
        'termsAndConditionsPublic',
    ),
);

const OfficeWorkshopPublicDetail = React.lazy(() =>
    lazyRetry(
        () => import('../components/calendar-public/workshop/OfficeWorkshopPublicDetail'),
        'officeWorkshopPublicDetail',
    ),
);

const OfficeWorkshopsPublic = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/workshop/OfficeWorkshopsPublic'), 'officeWorkshopsPublic'),
);

const WorkshopOrderResult = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/workshop/WorkshopOrderResult'), 'workshopOrderResult'),
);

const OfficeCoursePublic = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/course/OfficeCoursePublic'), 'officeCoursePublic'),
);

const CalendarPublic = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/CalendarPublic'), 'calendarPublic'),
);

const CourseOrderResult = React.lazy(() =>
    lazyRetry(() => import('../components/calendar-public/course/CourseOrderResult'), 'courseOrderResult'),
);

const PublicPagesRouterWrapper = styled.div<{ isList: boolean }>`
    ${props => {
        const { isList } = props;
        const maxWidth = !isList ? '1240' : '1024';

        return `
            max-width: ${maxWidth}px;
            width: 80vw;
            margin-left: auto;
            margin-right: auto;
        `;
    }}

    @media (max-width: 767px) {
        // Styl pro mobilní zařízení
        max-width: 100%;
        width: 100%;
        margin-left: 0;
        margin-right: 0;
        padding: 5px 5px 0 5px;
    }
`;

const PublicPagesRouter = () => {
    const match = useRouteMatch();
    const { isDesktop } = useViewport();
    const { path } = match;
    const { studioDomain } = useParams<{ studioDomain: string }>();
    const [identityContactDrawerVisible, setIdentityContactDrawerVisible] = useState(false);

    const { loading: loadingOfficeData, data: officeData } = useOfficeByDomain(studioDomain, true);

    useEffect(() => {
        if (officeData && officeData?.office) {
            // @ts-ignore
            currentPublicViewStudioVar({
                name: officeData.office.name,
                officeId: officeData.office.officeId,
                domain: officeData.office.domain,
            });
        } else {
            currentPublicViewStudioVar(null);
        }
        return () => {
            currentPublicViewStudioVar(null);
        };
    }, [officeData]);

    const handleIdentityContactDrawerVisible = (visible: boolean) => {
        setIdentityContactDrawerVisible(visible);
    };

    if (loadingOfficeData) {
        return <ContentLoading />;
    }

    if (officeData && officeData?.office) {
        const isList = officeData.office.officeConfig.publicCalendarView === CALENDAR_VIEW_LIST;

        return (
            <React.Suspense fallback={<ContentLoading />}>
                <PublicPagesRouterWrapper isList={isList}>
                    <IdentityHeader
                        office={officeData.office}
                        showCourses={officeData.showCourses}
                        showWorkshops={officeData.showWorkshops}
                        onIdentityContactDrawerVisible={handleIdentityContactDrawerVisible}
                        identityContactDrawerVisible={identityContactDrawerVisible}
                        isListAndNotMobile={isDesktop}
                    />
                    <Switch>
                        <PublicOfficeRoute exact path={[path, `${path}?from=:from&to=:to`]} office={officeData.office}>
                            <CalendarPublic office={officeData.office} isListAndNotMobile={isDesktop} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.pass}`} office={officeData.office}>
                            <PassSection office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.vouchers}`} office={officeData.office}>
                            <VouchersSection office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.claimVoucher}`} office={officeData.office}>
                            <ClaimVoucherSection office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.tos}`} office={officeData.office}>
                            <TermsAndConditionsPublic office={officeData.office} showCourses={officeData.showCourses} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.teacher}`} office={officeData.office}>
                            <TeacherProfile office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute
                            path={`${path}/${studioRoutes.workshops}/:workshopId/orders/:graphQlOrderId`}
                            office={officeData.office}
                        >
                            <WorkshopOrderResult office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute
                            path={`${path}/${studioRoutes.workshops}/:workshopId`}
                            office={officeData.office}
                        >
                            <OfficeWorkshopPublicDetail office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.workshops}`} office={officeData.office}>
                            <OfficeWorkshopsPublic office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute
                            path={`${path}/${studioRoutes.courses}/:courseId/orders/:graphQlOrderId`}
                            office={officeData.office}
                        >
                            <CourseOrderResult office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute
                            path={`${path}/${studioRoutes.courses}/:courseId`}
                            office={officeData.office}
                        >
                            <OfficeCoursePublicDetail office={officeData.office} />
                        </PublicOfficeRoute>
                        <PublicOfficeRoute path={`${path}/${studioRoutes.courses}`} office={officeData.office}>
                            <OfficeCoursePublic office={officeData.office} />
                        </PublicOfficeRoute>
                        <Redirect to={`${path}`} />
                    </Switch>
                </PublicPagesRouterWrapper>
            </React.Suspense>
        );
    }
    return <ErrorNotFoundStudio />;
};

export default PublicPagesRouter;
